import type { ReactNode } from 'react';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import type { FeatureKey } from '@oip/contracts';
import type { NavCountKey } from './navCounts';

/**
 * ⚠️ **`Administration` is still a section and no longer renders in the sidebar.** Its items carry a
 * `settingsGroup` and are drawn by the Settings area instead — they stay in `NAV_ITEMS` so the
 * breadcrumb, the command palette and `PlatformHome` keep finding them by name.
 *
 * `Fleet` and `Insights` replace `Operations` and `Intelligence`: sections named for the reader's
 * job rather than for the technology, which is what made "Intelligence" and "Automation & AI"
 * indistinguishable.
 */
export type NavSection = 'Fleet' | 'Insights' | 'Administration' | 'Partner' | 'Platform';

/**
 * The five groups of the Settings area.
 *
 * ⚠️ **The navigation redesign proposed THREE — Administration, AI & automation, Workspace — and
 * measured against the real menu that holds ten of fifteen pages.** `Provider Connections`,
 * `Inbound events`, `Audit`, `Geofences`, `Vehicle classes`, `Drivers' hours rulesets`,
 * `Import vehicles`, `Scheduled reports`, `My Notifications` and `Sub-tenants` had no slot. Five
 * groups hold all fifteen without inventing a page or dropping one.
 *
 * ORDER IS THE READING ORDER, most-used first: who may use this, then the reference data a fleet
 * sets up once, then the plumbing somebody opens when a feed dies, then how people are told, then
 * the two AI knobs an administrator touches rarely.
 */
export const SETTINGS_GROUPS = [
  'Organisation',
  'Fleet setup',
  'Data & integrations',
  'Notifications',
  'AI & automation',
] as const;

export type SettingsGroup = (typeof SETTINGS_GROUPS)[number];

export interface NavItem {
  label: string;
  path: string;
  icon: ReactNode;
  /**
   * A short word beside the label — "Beta" — for a screen that works and is
   * not yet finished.
   *
   * ⚠️ **DISPLAY ONLY, and deliberately not part of `label`.** The label is the
   * command palette's entry, the breadcrumb's word and the link's accessible
   * name; folding "Beta" into it would put the word in all three and change what
   * a screen reader announces and what a test finds by name. The chip is
   * `aria-hidden` for the same reason the copilot's question cards carry an
   * explicit `aria-label` — a decoration must not become part of the name.
   */
  badge?: string;
  /**
   * An ATTENTION count beside the label — "how many of these need me right now".
   *
   * ⚠️ **AN ATTENTION COUNT, NEVER A ROW COUNT, and the two disagree by design.** Both queues
   * default to UNFILTERED, so the page behind `Recommendations` lists every recommendation ever
   * generated — measured on the development tenant, **47 rows against a badge of 4**. That is not
   * a mis-stated total: a menu badge means outstanding-and-yours everywhere it appears, and the
   * alternative — counting the whole table — is a number nobody can act on that only grows. What
   * makes it honest is the tooltip SAYING which count it is.
   *
   * ⚠️ **DISPLAY ONLY and `aria-hidden`, for the reason `badge` above is — plus a sharper one.**
   * Two e2e specs find this item with `getByRole('link', { name: 'Incidents' })`, an EXACT match,
   * so folding the number into the accessible name would make them pass or fail according to how
   * much data the database happened to hold — a flake tied to seed state, which is worse than a
   * break. The count is still announced, through `aria-describedby`: a DESCRIPTION rather than a
   * name, so every existing selector is untouched.
   *
   * The value names WHICH count and never the number. The figure is point-in-time and belongs to
   * the fleet summary; a static menu declaration cannot hold it.
   */
  attentionCount?: NavCountKey;
  /**
   * One line saying what this page is FOR — required on every Settings item.
   *
   * ⚠️ **THE LARGEST DEFECT ON THAT PAGE WAS THAT IT TOLD A READER NOTHING.** Fifteen cards and
   * fifteen words: somebody arriving at `Correlation Settings` or `Provider Connections` for the
   * first time had to open each one to find out what it was. A launcher of bare names is a menu
   * with extra steps, and it is what made the page read as unfinished.
   *
   * ⚠️ **TAKEN FROM THE PAGE'S OWN WORDS wherever it has them**, condensed to one line — eight
   * of the eighteen already carried an `AdminPageLayout` description written for a reader. A
   * second, freshly-invented sentence beside the same page is how a card and the page it opens
   * come to describe different things.
   *
   * Sidebar items carry none: the label is the whole row there, and a description under every
   * entry is the clutter the redesign existed to remove.
   */
  description?: string;
  /** Tenant roles permitted to see this item. Omitted = any authenticated role. */
  roles?: string[];
  /**
   * Platform-scoped item. Visibility depends solely on `isPlatformAdmin` —
   * never on a tenant role, because platform authority is a capability on the
   * user, not a membership role (ADR 0009).
   */
  platform?: boolean;
  /**
   * Platform-OWNED data that a tenant may also read.
   *
   * Unlike `platform`, this item still appears for tenant users. It adds one
   * thing: a platform administrator sees it WITHOUT having entered a tenant,
   * because the data has no tenant. The prompt registry needed it — authoring
   * is platform-only, and the page was reachable only by typing the URL for
   * exactly the role permitted to use it.
   */
  platformOwned?: boolean;
  /**
   * Feature entitlement this item needs.
   *
   * Display only. The route is blocked and the API refuses independently —
   * hiding a link has never stopped anyone typing a URL.
   */
  feature?: FeatureKey;
  /**
   * Lives in the Settings area rather than the sidebar.
   *
   * ⚠️ **THE ITEM STAYS IN `NAV_ITEMS`, and that is the whole design.** Thirteen files read this
   * list: `Breadcrumbs` resolves a first-segment crumb from it, the command palette offers every
   * item by name, `PlatformHome` builds its "where to go" card from it, and `tenantlessRoutes`
   * reads it. REMOVING an administration page to put it in Settings would take its breadcrumb
   * label with it — the trail would fall back to `titleCase('providers')` — and would make it
   * unsearchable. So membership is unchanged and only the SIDEBAR filters on this.
   *
   * `visibleNavItems` is the one place that decides, so the palette and the breadcrumbs cannot
   * drift from the menu.
   */
  settingsGroup?: SettingsGroup;
  /**
   * White-label PARTNER item: shown only to somebody who administers one.
   *
   * A fourth gate rather than a role, because partner authority is neither a
   * tenant role nor the platform capability — it is a membership in a partner,
   * which is the third authorization surface. Folding it into `roles` would
   * offer the partner console to every TENANT_ADMIN on the platform, and
   * folding it into `platform` would hide it from the only people who may
   * use it.
   *
   * Like every gate here it is PRESENTATION ONLY; `WhiteLabelContextGuard`
   * decides.
   */
  whiteLabel?: boolean;
  /**
   * False while the destination page does not exist yet. The navigation model
   * carries the route so later stages only flip this flag, but an unavailable
   * item is never rendered — a menu entry leading nowhere is worse than none.
   */
  available?: boolean;
  section: NavSection;
}

export const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <DashboardOutlinedIcon />, section: 'Fleet' },
  {
    /*
     * ⚠️ Directly under the dashboard, because it answers the question somebody
     * arrives with — "what do I need to know" — and the dashboard answers the
     * one they ask second. Ungated: it carries nothing the incident queue does
     * not, and a news page half the operations team cannot open is not a news
     * page.
     */
    label: 'Fleet news',
    path: '/news',
    icon: <NewspaperOutlinedIcon />,
    section: 'Fleet',
  },
  {
    label: 'Incidents',
    path: '/incidents',
    icon: <WarningAmberOutlinedIcon />,
    section: 'Fleet',
    attentionCount: 'incidents',
  },
  {
    label: 'Recommendations',
    path: '/recommendations',
    icon: <LightbulbOutlinedIcon />,
    section: 'Fleet',
    attentionCount: 'recommendations',
  },
  {
    label: 'AI Assistant',
    path: '/copilot',
    icon: <AssistantOutlinedIcon />,
    feature: 'AI_COPILOT',
    /*
     * v1 is these two roles only (R10a). OPERATOR and VIEWER come later as a
     * product decision, not a security one — D16 already ships, so enabling a
     * role stays a toggle rather than a review.
     */
    roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
    section: 'Insights',
  },
  {
    label: 'Outcomes',
    path: '/recommendations/outcomes',
    icon: <InsightsOutlinedIcon />,
    section: 'Insights',
  },
  {
    label: 'Vehicle classes',
    path: '/vehicles/classes',
    description: 'Cohorts of comparable vehicles. Comparison is computed only within a class.',
    icon: <CategoryOutlinedIcon />,
    section: 'Administration',
    settingsGroup: 'Fleet setup',
  },
  {
    /*
     * ⚠️ BOTH gates, matching the route and the controller. `TENANT_ADMIN` alone is narrower
     * than the New vehicle dialog, and `isNavItemVisible` applies all three gates — so an
     * OPERATIONS_MANAGER is not offered a screen whose every press would 403, which is the
     * defect that rule exists to prevent.
     */
    label: 'Import vehicles',
    path: '/vehicles/import',
    description: 'Create vehicles and drivers from one spreadsheet, with a dry run first.',
    icon: <UploadFileOutlinedIcon />,
    feature: 'BULK_VEHICLE_IMPORT',
    roles: ['TENANT_ADMIN'],
    section: 'Administration',
    settingsGroup: 'Fleet setup',
  },
  {
    /*
     * ⚠️ Gated on TACHOGRAPH, matching the server and the route. A ruleset changes only the
     * daily and weekly limit alerts, and those read TACHO-tier counters a tenant without the
     * feature never stores — so for them the screen could accept a limit and change nothing,
     * which reads as a broken product rather than as an entitlement they do not hold.
     */
    label: 'Drivers’ hours rulesets',
    path: '/vehicles/drivers-hours-rulesets',
    description: 'The daily and weekly limits a vehicle is judged against.',
    icon: <ScheduleOutlinedIcon />,
    feature: 'TACHOGRAPH',
    section: 'Administration',
    settingsGroup: 'Fleet setup',
  },
  {
    label: 'CAN intelligence',
    path: '/operations/can-intelligence',
    icon: <SensorsOutlinedIcon />,
    feature: 'CAN_TELEMETRY',
    section: 'Insights',
  },
  {
    label: 'CAN operations',
    path: '/operations/can-operations',
    icon: <SpeedOutlinedIcon />,
    feature: 'CAN_TELEMETRY',
    section: 'Insights',
  },
  {
    /*
     * Drivers' hours across the fleet. Beside the CAN pages because it is the
     * same kind of thing — a hardware tier with its own screens — and gated the
     * same way, so a tenant without a tachograph never sees it.
     */
    label: 'Tachograph',
    path: '/operations/tachograph',
    icon: <ScheduleOutlinedIcon />,
    feature: 'TACHOGRAPH',
    section: 'Fleet',
  },
  {
    /*
     * ⚠️ Gated on ANOMALY_DETECTION, so a tenant without it never sees the item
     * — `isNavVisible` applies all three gates and a menu listing screens you
     * may not open is not a menu. Beside the CAN and tachograph pages because it
     * is the same kind of thing: a capability with its own screen.
     */
    label: 'Anomalies',
    path: '/operations/anomalies',
    icon: <InsightsOutlinedIcon />,
    /*
     * ⚠️ BETA, and it is a statement about the FEATURE rather than the screen.
     * The detector runs in shadow mode — nothing it finds is published, and its
     * thresholds have never been calibrated against a real fleet. A reader who
     * takes these findings as settled would be taking them further than the
     * platform does.
     */
    badge: 'Beta',
    feature: 'ANOMALY_DETECTION',
    section: 'Insights',
  },
  {
    label: 'Vehicles',
    path: '/vehicles',
    icon: <LocalShippingOutlinedIcon />,
    section: 'Fleet',
  },
  {
    label: 'Drivers',
    path: '/drivers',
    icon: <BadgeOutlinedIcon />,
    section: 'Fleet',
  },
  { label: 'Policies', path: '/policies', icon: <PolicyOutlinedIcon />, section: 'Insights' },
  {
    label: 'Prompt Registry',
    path: '/prompts',
    description: 'The wording the platform asks a model for, and its versions.',
    icon: <AutoAwesomeOutlinedIcon />,
    section: 'Administration',
    settingsGroup: 'AI & automation',
    /*
     * Platform-owned data, so a platform administrator reaches it WITHOUT
     * entering a tenant. Without this the page was unreachable by navigation
     * for the only role that may author a prompt — it was findable only by
     * typing the URL.
     */
    platformOwned: true,
  },
  {
    label: 'LLM Executions',
    path: '/executions',
    description: 'Every model call this organisation made, and what it cost.',
    icon: <TerminalOutlinedIcon />,
    section: 'Administration',
    settingsGroup: 'AI & automation',
  },
  {
    label: 'Audit',
    path: '/audit',
    description: 'Every change, who made it, and from where.',
    icon: <FactCheckOutlinedIcon />,
    roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
    section: 'Administration',
    settingsGroup: 'Data & integrations',
  },
  {
    label: 'Provider Connections',
    path: '/admin/providers',
    description: 'Telemetry feeds, the credentials they use, and what they have sent.',
    icon: <CableOutlinedIcon />,
    roles: ['TENANT_ADMIN'],
    section: 'Administration',
    settingsGroup: 'Data & integrations',
  },
  {
    /*
     * Beside Provider Connections, which is where the feed is configured and
     * where its health is reported. This is the same subject one level down:
     * not "is the feed working" but "what exactly did it send".
     */
    label: 'Inbound events',
    path: '/admin/inbound-events',
    description: 'Every payload your provider sent, exactly as it arrived.',
    icon: <InboxOutlinedIcon />,
    roles: ['TENANT_ADMIN'],
    section: 'Administration',
    settingsGroup: 'Data & integrations',
  },
  {
    label: 'Scheduled reports',
    path: '/admin/reports',
    description: 'Recurring summaries of a finished period, sent to people you name.',
    icon: <SummarizeOutlinedIcon />,
    roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
    feature: 'SCHEDULED_REPORTS',
    section: 'Administration',
    settingsGroup: 'Data & integrations',
  },
  {
    label: 'Branding',
    path: '/admin/branding',
    description: 'Your name, logo and colours, on the product and on every notification sent.',
    icon: <PaletteOutlinedIcon />,
    roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
    feature: 'WHITE_LABEL',
    section: 'Administration',
    /*
     * ⚠️ **`Organisation`, and it was filed under `Notifications` — a mis-grouping, reported.**
     * It landed there because a company's `fromEmail` is branding and mail is notifications;
     * that is one field out of nine. The page sets a display name, a logo, a favicon, colours
     * and support contacts — the organisation's IDENTITY, which is what `Tenant Settings`,
     * `Members` and `Sub-tenants` beside it are about.
     *
     * ⚠️ **TENANT-FACING, and deliberately NOT moved to the Platform section.** That was the
     * literal ask and it costs a shipped capability: `platform: true` is decided solely by
     * `isPlatformAdmin`, so the four companies holding `WHITE_LABEL` today would lose their own
     * branding link — and `BRANDING_FIELD_LEVEL` exists precisely to let them set it, giving a
     * FLEET ACCOUNT its accent and a COMPANY its name and sender. A platform administrator
     * reaches the same page by entering the tenant, which is how it was found in the first place.
     */
    settingsGroup: 'Organisation',
  },
  {
    label: 'Notification Policy',
    path: '/admin/notifications',
    description: 'What this tenant sends, to whom, and when.',
    icon: <CampaignOutlinedIcon />,
    roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
    section: 'Administration',
    settingsGroup: 'Notifications',
  },
  {
    label: 'My Notifications',
    path: '/account/notifications',
    description: 'Your own alerts, and the channels you receive them on.',
    icon: <NotificationsNoneOutlinedIcon />,
    // No `roles`: every authenticated role, deliberately. See the route comment.
    section: 'Administration',
    settingsGroup: 'Notifications',
  },
  {
    label: 'Tenant Settings',
    path: '/admin/settings',
    description: 'Units, working days, and the AI allowance for this organisation.',
    icon: <SettingsOutlinedIcon />,
    roles: ['TENANT_ADMIN'],
    section: 'Administration',
    settingsGroup: 'Organisation',
  },
  {
    /*
     * Beside Correlation Settings, where what a zone MEANS is configured. The
     * classification here and the classification map there are two halves of one
     * decision, and separating them would hide that.
     */
    label: 'Geofences',
    path: '/admin/geofences',
    description: 'Zones your events refer to, and what leaving one means.',
    icon: <PlaceOutlinedIcon />,
    section: 'Administration',
    settingsGroup: 'Fleet setup',
    roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
  },
  {
    label: 'Correlation Settings',
    path: '/admin/correlation',
    description: 'How incoming events are grouped into incidents.',
    icon: <TuneOutlinedIcon />,
    section: 'Administration',
    settingsGroup: 'AI & automation',
    roles: ['TENANT_ADMIN'],
  },
  {
    // "Members", not "Users": a tenant administrator administers memberships of
    // global identities, never the identities themselves (ADR 0009).
    label: 'Members',
    path: '/admin/members',
    description: 'People with access to this tenant, and what each of them may do.',
    icon: <GroupOutlinedIcon />,
    roles: ['TENANT_ADMIN'],
    section: 'Administration',
    settingsGroup: 'Organisation',
  },
  {
    /*
     * Shown to every tenant administrator, not only in a parent context.
     *
     * Hiding it inside a branch would be defensible — the routes are refused
     * there — but a person who administers an organisation lands in its DEFAULT
     * sub-tenant, so the item would be invisible exactly when they went looking
     * for it. The page itself explains where the controls live and offers the
     * one click that gets there.
     */
    label: 'Sub-tenants',
    path: '/admin/sub-tenants',
    description: 'Branches, depots or regions within your organisation.',
    icon: <AccountTreeOutlinedIcon />,
    roles: ['TENANT_ADMIN'],
    section: 'Administration',
    settingsGroup: 'Organisation',
  },

  // ── Partner ───────────────────────────────────────────────────────────────
  // The third authorization surface. Visible only to somebody who administers a
  // white-label partner, which is neither a tenant role nor a platform
  // capability.
  {
    label: 'Brand',
    path: '/partner/branding',
    icon: <PaletteOutlinedIcon />,
    whiteLabel: true,
    available: true,
    section: 'Partner',
  },
  {
    /*
     * Not folded into Brand: brand is what the product LOOKS like, units are
     * what a number MEANS to the reader — a fact about their country. Filing
     * them together is how "Brand" becomes the page unrelated partner-wide
     * settings accumulate on.
     */
    label: 'Regional defaults',
    path: '/partner/units',
    icon: <StraightenOutlinedIcon />,
    whiteLabel: true,
    available: true,
    section: 'Partner',
  },
  {
    label: 'People',
    path: '/partner/people',
    icon: <BadgeOutlinedIcon />,
    whiteLabel: true,
    available: true,
    section: 'Partner',
  },
  {
    /*
     * Above Companies, because a reseller is what a company is filed UNDER — the reader
     * sets up the level before using it. ⚠️ A new partner nav item is a DECLARED list and
     * `navVisibility.test.ts` fires on an addition, deliberately: a further item gaining
     * the flag should be a decision rather than a drift.
     */
    label: 'Resellers',
    path: '/partner/resellers',
    icon: <HandshakeOutlinedIcon />,
    whiteLabel: true,
    available: true,
    section: 'Partner',
  },
  {
    /*
     * ⚠️ **Offered to a RESELLER administrator too**, so it is listed in
     * `RESELLER_PARTNER_PATHS` — the declared mirror of `@ResellerScoped()` on the server.
     * Safe because the server fixes `partner_code`, and `reseller_name` for a
     * reseller-scoped caller, from the SESSION: a file naming a sibling's reseller has that
     * column DROPPED rather than honoured (D8/D15).
     */
    label: 'Import customers',
    path: '/partner/imports',
    icon: <UploadFileOutlinedIcon />,
    whiteLabel: true,
    available: true,
    section: 'Partner',
  },
  {
    label: 'Companies',
    path: '/partner/companies',
    icon: <StorefrontOutlinedIcon />,
    whiteLabel: true,
    available: true,
    section: 'Partner',
  },

  // ── Platform ──────────────────────────────────────────────────────────────
  // Registered now so later stages only set `available: true`. None render yet.
  {
    // First, because a partner sits ABOVE a tenant: the Tenants list beneath it
    // shows every customer on the platform, partnered or not.
    /*
     * ⚠️ "Partners", not "White-label Partners". The qualifier described the TIER to
     * whoever built it; the reader is a platform administrator for whom there is only one
     * kind of partner, and the shorter word is what they call them. The ROUTE stays
     * `/platform/white-labels` — a URL people have bookmarked and the API's own vocabulary
     * — so this is a label change and nothing else.
     */
    label: 'Partners',
    path: '/platform/white-labels',
    icon: <StorefrontOutlinedIcon />,
    platform: true,
    available: true,
    section: 'Platform',
  },
  {
    label: 'Tenants',
    path: '/platform/tenants',
    icon: <ApartmentOutlinedIcon />,
    platform: true,
    available: true, // UI-2
    section: 'Platform',
  },
  {
    /*
     * ⚠️ The only surface that may create a PARTNER, so it sits with the platform's own
     * screens rather than beside the tenant import. Platform-gated, not feature-gated:
     * `BULK_VEHICLE_IMPORT` is a TENANT entitlement and the platform holds none.
     */
    label: 'Import hierarchy',
    path: '/platform/imports',
    icon: <UploadFileOutlinedIcon />,
    platform: true,
    available: true,
    section: 'Platform',
  },
  {
    label: 'Users',
    // "Users" IS correct here — platform administrators manage global identities.
    path: '/platform/users',
    icon: <BadgeOutlinedIcon />,
    platform: true,
    available: true, // UI-3
    section: 'Platform',
  },
  {
    // Beside Users, because it is the same question asked the other way round:
    // Users is "who exists", this is "who is signed in".
    label: 'Active Sessions',
    path: '/platform/sessions',
    icon: <DevicesOutlinedIcon />,
    platform: true,
    available: true,
    section: 'Platform',
  },
  {
    label: 'Platform Audit',
    path: '/platform/audit',
    icon: <ShieldOutlinedIcon />,
    platform: true,
    available: true, // UI-6
    section: 'Platform',
  },
  {
    // Platform-scoped because none of what it reports has a tenant dimension:
    // partition headroom belongs to the table, and the rollup is one job for
    // the whole platform.
    label: 'Notification Channels',
    path: '/platform/notifications',
    description: 'Platform-wide delivery. Every change here affects all tenants.',
    icon: <CampaignOutlinedIcon />,
    platform: true,
    available: true,
    section: 'Platform',
    /*
     * ⚠️ **IN SETTINGS, NOT THE PLATFORM SIDEBAR, and `platform: true` still decides WHO.**
     * `settingsGroup` moves where an item is LISTED; it changes no gate, so this remains
     * visible to a platform administrator and to nobody else — `settingsNavItems` applies the
     * same `isNavItemVisible` the sidebar does.
     *
     * It belongs beside the other two: the CHANNELS a message leaves on, the POLICY for what
     * goes out, and MY own preferences are one subject read together. In the Platform section
     * it sat among tenant and partner administration, which is a different job.
     */
    settingsGroup: 'Notifications',
  },
  {
    label: 'Pipeline Health',
    path: '/platform/pipeline-health',
    icon: <MonitorHeartOutlinedIcon />,
    platform: true,
    available: true,
    section: 'Platform',
  },
  {
    /*
     * ⚠️ FIRST of the logging group, because it is the page that says whether
     * anything needs doing. The dashboard and the search answer "what is
     * happening" and "why"; this one answers "is anybody required", which is the
     * question somebody opens the platform section to ask.
     */
    label: 'Alerts',
    path: '/platform/alerts',
    icon: <NotificationsActiveOutlinedIcon />,
    platform: true,
    available: true,
    section: 'Platform',
  },
  {
    /*
     * ⚠️ BEFORE System Logs, because it is the page somebody lands on and the
     * search is where they go NEXT — every panel on it drills through to a
     * filtered search. A dashboard filed after the tool it feeds reads as an
     * afterthought.
     */
    label: 'Log Dashboard',
    path: '/platform/log-dashboard',
    icon: <InsightsOutlinedIcon />,
    platform: true,
    available: true,
    section: 'Platform',
  },
  {
    /*
     * ⚠️ Beside Pipeline Health deliberately: that page says whether a scheduled
     * job RAN, this one says what any service SAID, and the second question is
     * where somebody goes when the first one is not enough.
     */
    label: 'System Logs',
    path: '/platform/logs',
    icon: <ArticleOutlinedIcon />,
    platform: true,
    available: true,
    section: 'Platform',
  },
];

export const SECTION_ORDER = [
  'Fleet',
  'Insights',
  /*
   * ⚠️ Kept in the order so nothing that iterates sections loses it, and rendered by NOTHING in the
   * sidebar: every Administration item now carries a `settingsGroup`, and `visibleNavItems` filters
   * those out. A section that renders empty draws no heading, which is the existing behaviour.
   */
  'Administration',
  // Above Platform, below Administration: a partner administrator is a customer
  // of the platform rather than an operator of it, and most sessions that see
  // this section see no Platform section at all.
  'Partner',
  'Platform',
] as const;
